import type { StorageLike } from './types';

const STANDARD_EXCEPTION_NAMES = new Set([
  'Error',
  'TypeError',
  'RangeError',
  'SyntaxError',
  'QuotaExceededError',
  'SecurityError',
  'DataCloneError',
  'InvalidStateError',
  'NotSupportedError',
  'NotAllowedError',
  'AbortError',
  'UnknownError',
  'WorkspaceRecoveryConflictError',
]);

export type StorageFailureDiagnostic = {
  cause: unknown;
  operation: 'setItem' | 'removeItem' | 'serialize' | 'transaction';
  category:
    | 'business'
    | 'workspace-recovery'
    | 'workspace-preferences'
    | 'other';
  exceptionName: string;
  keyLength: number;
  valueLength?: number;
};

/** Keep the original exception, but never display its potentially private message/key. */
export function storageFailureDiagnostic(
  cause: unknown,
  operation: StorageFailureDiagnostic['operation'],
  key: string,
  value?: string,
): StorageFailureDiagnostic {
  const name =
    cause && typeof cause === 'object' && 'name' in cause
      ? cause.name
      : undefined;
  return {
    cause,
    operation,
    category: key.startsWith('quotation-system:formal-workspace-session:')
      ? 'workspace-recovery'
      : key === 'cpq-v2-workspace-ui:v1'
        ? 'workspace-preferences'
        : /^cpq-v2-demo:v[345]$/.test(key)
          ? 'business'
          : 'other',
    exceptionName:
      typeof name === 'string' && STANDARD_EXCEPTION_NAMES.has(name)
        ? name
        : 'UnknownError',
    keyLength: key.length,
    ...(value !== undefined ? { valueLength: value.length } : {}),
  };
}

export function storageFailureSummary(
  failure: StorageFailureDiagnostic,
): string {
  return `${failure.category} / ${failure.operation} / ${failure.exceptionName}；键长度 ${failure.keyLength}${failure.valueLength === undefined ? '' : `，值长度 ${failure.valueLength}`}`;
}

/** Only the disposable business repository uses this fail-closed latch. */
export function createFailClosedBusinessStorage(
  storage: StorageLike,
  onFailure: (failure: StorageFailureDiagnostic) => void,
): StorageLike {
  let firstFailure: StorageFailureDiagnostic | undefined;
  const write = (
    operation: 'setItem' | 'removeItem',
    key: string,
    value?: string,
  ) => {
    if (firstFailure)
      throw new Error('Disposable persistence is blocked', {
        cause: firstFailure,
      });
    try {
      if (operation === 'setItem') storage.setItem(key, value ?? '');
      else storage.removeItem(key);
    } catch (cause) {
      firstFailure = storageFailureDiagnostic(cause, operation, key, value);
      onFailure(firstFailure);
      throw cause;
    }
  };
  return {
    getItem: (key) => storage.getItem(key),
    setItem: (key, value) => write('setItem', key, value),
    removeItem: (key) => write('removeItem', key),
  };
}
